/*
  HelpMenu — the top bar's "?" (02 #9 shell: search box, bell, help). A small popover with the shortcuts and the
  places people ask about most: searching everything, undoing a change, and (for admins) which outside services
  are set up. Nothing here calls the server.

    <HelpMenu onSearch={palette.show} />
*/
import { Link } from 'react-router-dom';
import { CircleHelp, History, PlugZap, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { hasPermission } from '@/lib/permissions';
import { INTEGRATIONS_ADMIN_PATH } from '@/lib/integrations';
import { commandPaletteShortcutLabel } from '@/components/ui/command-palette';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

function Row({ icon: Icon, title, children }) {
  return (
    <li className="flex gap-3 px-4 py-2.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
      <div className="min-w-0 text-[12.5px] leading-snug text-ink-3">
        <p className="text-[13px] font-semibold text-ink">{title}</p>
        {children}
      </div>
    </li>
  );
}

export function HelpMenu({ onSearch, className }) {
  const { user } = useAuth() || {};
  const isAdmin = hasPermission(user, 'admin');
  const shortcut = commandPaletteShortcutLabel();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'grid h-9 w-9 place-items-center rounded-control text-ink-2 transition-colors hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className
          )}
          aria-label="Help"
          data-testid="topbar-help"
        >
          <CircleHelp className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] p-0" data-testid="topbar-help-panel">
        <p className="border-b border-line-2 px-4 py-2.5 text-[13.5px] font-bold text-ink">Help</p>
        <ul className="divide-y divide-[color:var(--line-2)] py-1">
          <Row icon={Search} title="Search everything">
            Projects, tasks, part numbers, LPOs and drawings —{' '}
            <button type="button" onClick={onSearch} className="font-semibold text-brand-ink hover:underline">
              press <kbd className="rounded border border-line bg-surface-2 px-1 font-mono text-[11px] text-ink-2">{shortcut}</kbd>
            </button>
            .
          </Row>
          <Row icon={History} title="Undo a change">
            Every saved change offers Undo in its message. Older ones: your account menu › Recent changes.
          </Row>
          {isAdmin ? (
            <Row icon={PlugZap} title="Something says “not set up”?">
              <Link to={INTEGRATIONS_ADMIN_PATH} className="font-semibold text-brand-ink hover:underline">Admin › Integrations</Link>
              {' '}lists every outside service, what IT must provide and its switch.
            </Row>
          ) : null}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export default HelpMenu;
